import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { lessonsAPI } from '../api';
import { getErrorMessage } from '@/core/api/response-handler';

/**
 * Mutations hook for Lesson Answers
 *
 * Provides all mutation operations for lesson answer management.
 * Follows TanStack Query best practices by separating mutations from queries.
 *
 * Features:
 * - Approve/reject answer mutations
 * - Delete answer mutation
 * - Consistent error handling
 * - Automatic query invalidation
 * - Toast notifications
 *
 * @example
 * ```tsx
 * const mutations = useLessonAnswersMutations();
 *
 * // Approve an answer with points
 * await mutations.approve.mutateAsync({ id: answerId, points: 10 });
 *
 * // Reject an answer with reason
 * await mutations.reject.mutateAsync({ id: answerId, reason: 'Invalid submission' });
 *
 * // Delete an answer
 * await mutations.deleteAnswer.mutateAsync({ userId, lessonId, editionId });
 *
 * // Check loading state
 * if (mutations.isLoading) { ... }
 * ```
 */
export function useLessonAnswersMutations() {
    const queryClient = useQueryClient();

    /**
     * Approve an answer mutation
     *
     * Updates answer approval status to approved with points.
     * Invalidates report queries on success.
     */
    const approve = useMutation({
        mutationFn: async ({ id, points, fise }: { id: string; points?: number; fise?: number }) => {
            return lessonsAPI.updateApproval(id, true, undefined, points, fise);
        },
        onSuccess: () => {
            toast.success('Răspunsul a fost aprobat cu succes');
            queryClient.invalidateQueries({ queryKey: ['lessons', 'answers', 'report'] });
            queryClient.invalidateQueries({ queryKey: ['lessons', 'answers'] });
        },
        onError: (error) => {
            toast.error(
                error?.message ||
                    'Eroare la aprobarea răspunsului'
            );
        },
    });

    /**
     * Reject an answer mutation
     *
     * Updates answer approval status to rejected with a reason.
     * Invalidates report queries on success.
     */
    const reject = useMutation({
        mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
            return lessonsAPI.updateApproval(id, false, reason || 'Răspuns respins');
        },
        onSuccess: () => {
            toast.success('Răspunsul a fost respins');
            queryClient.invalidateQueries({ queryKey: ['lessons', 'answers', 'report'] });
            queryClient.invalidateQueries({ queryKey: ['lessons', 'answers'] });
        },
        onError: (error) => {
            toast.error(
                error?.message ||
                    'Eroare la respingerea răspunsului'
            );
        },
    });

    /**
     * Delete an answer mutation
     *
     * Permanently deletes a lesson answer.
     * Invalidates report queries on success.
     */
    const deleteAnswer = useMutation({
        mutationFn: async ({
            userId,
            lessonId,
            editionId,
        }: {
            userId: string;
            lessonId: string;
            editionId: string;
        }) => {
            return lessonsAPI.deleteAnswer(userId, lessonId, editionId);
        },
        onSuccess: () => {
            toast.success('Răspunsul a fost șters cu succes');
            queryClient.invalidateQueries({ queryKey: ['lessons', 'answers', 'report'] });
            queryClient.invalidateQueries({ queryKey: ['lessons', 'answers'] });
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la ștergerea răspunsului'));
        },
    });

    return {
        // Mutations
        approve,
        reject,
        deleteAnswer,

        // Individual loading states
        isApproving: approve.isPending,
        isRejecting: reject.isPending,
        isDeleting: deleteAnswer.isPending,

        // Combined loading state (useful for disabling all actions)
        isLoading: approve.isPending || reject.isPending || deleteAnswer.isPending,
    };
}
